import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheckDouble, faCircleInfo, faGripDotsVertical, faLayerGroup } from '@fortawesome/pro-solid-svg-icons';

import { Brief, Module } from 'shared/typings/briefs';

export type BriefPdfSectionId = 'Overview' | 'Creator Requirements' | 'Content Guidelines' | 'Module Requirements';

export interface BriefPdfSection {
  id: BriefPdfSectionId;
  title: string;
  icon: IconDefinition;
}

/**
 * Sections of the brief that must never reach the exported PDF.
 * Pricing is internal-only, so it is excluded regardless of the viewer's permissions.
 */
export const BRIEF_PDF_EXCLUDED_SECTIONS = ['Pricing'];

/**
 * Sections of the brief included in the exported PDF, in the order they are printed.
 * Titles and icons mirror the on-screen brief so the PDF reads the same way.
 */
const BRIEF_PDF_SECTIONS: BriefPdfSection[] = [
  { id: 'Overview', title: 'Overview', icon: faLayerGroup },
  { id: 'Creator Requirements', title: 'Creator Requirements', icon: faCircleInfo },
  { id: 'Content Guidelines', title: 'Content Guidelines', icon: faCheckDouble },
  { id: 'Module Requirements', title: 'Module Requirements', icon: faGripDotsVertical },
];

export const getBriefPdfSections = (): BriefPdfSection[] =>
  BRIEF_PDF_SECTIONS.filter(({ title }) => !BRIEF_PDF_EXCLUDED_SECTIONS.includes(title));

/**
 * Every module of the brief, in module order. The on-screen brief shows one module per tab,
 * but the PDF must contain all of them.
 */
export const getBriefPdfModules = (brief: Brief): Module[] =>
  [...(brief?.modules || [])].sort((a, b) => a.moduleNum - b.moduleNum);

const FILE_NAME_UNSAFE_CHARACTERS = /[|/\\:*?"<>]/g;

/**
 * Document title used while printing. Browsers derive the suggested file name from it,
 * so characters they reject in a file name are replaced.
 */
export const getBriefPdfTitle = (brief: Brief): string => {
  const name = (brief?.name || '').replace(FILE_NAME_UNSAFE_CHARACTERS, '-').trim();

  return name ? `${name} Brief` : 'Unnamed Brief';
};
